/**
 * Option Chain — strikes around the money with LTP, OI, OI change, PCR and
 * max pain, plus a builder that turns clicked strikes into a multi-leg
 * strategy with payoff, margin and one-click placement.
 *
 * The REST chain is refreshed every 15s (OI baseline, metrics); the
 * websocket feed streams LTP/OI for the visible strikes in between.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bookmark, Layers, RefreshCw, Search, Trash2, Wifi, WifiOff } from 'lucide-react';
import { api, getErrorMessage } from '@/config/api';
import { cn } from '@/lib/cn';
import { formatDateTime, formatNumber, formatPct, formatQty, signClass, toNum } from '@/lib/format';
import { PRESETS, buildPreset, newLegId } from '@/lib/options';
import type { PresetKind, StrategyLeg } from '@/lib/options';
import { useMarketStore } from '@/stores/market-store';
import { useMarketWebSocket } from '@/hooks/use-websocket';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TableMessage,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { StrategyBuilder } from '@/features/options/components/StrategyBuilder';

// ── Types ───────────────────────────────────────────────────────────────────

interface ChainQuote {
  instrument_token: string;
  trading_symbol: string;
  exchange_segment: string;
  ltp: string;
  change_percent: string;
  oi: string;
  oi_change: string;
  volume: string;
  bid: string;
  ask: string;
  in_the_money: boolean;
}

interface ChainRow {
  strike: string;
  is_atm: boolean;
  ce: ChainQuote | null;
  pe: ChainQuote | null;
}

interface ChainData {
  underlying: string;
  segment: string;
  expiry: string;
  expiry_label: string;
  spot: string | null;
  spot_source: string;
  atm_strike: string | null;
  lot_size: number;
  tick_size: string;
  pcr: string | null;
  max_pain: string | null;
  support: string | null;
  resistance: string | null;
  total_ce_oi: number;
  total_pe_oi: number;
  strikes_total: number;
  rows: ChainRow[];
  tokens: string[];
  updated_at: string;
}

interface ExpiryInfo {
  expiry: string;
  label: string;
  days_to_expiry: number;
}

interface UnderlyingInfo {
  name: string;
  segment: string;
  kind: string;
  lot_size: number;
}

interface SavedStrategy {
  id: string;
  name: string;
  underlying: string;
  segment: string;
  expiry: string;
  legs: Array<{ side: 'B' | 'S'; option_type: 'CE' | 'PE'; strike: string; lots: number }>;
  created_at: string;
}

interface BrokerStatus {
  connection_id: string;
  display_name: string;
  is_active: boolean;
  is_session_valid: boolean;
}

const QUICK_PICKS: Array<{ name: string; segment: string }> = [
  { name: 'NIFTY', segment: 'nse_fo' },
  { name: 'BANKNIFTY', segment: 'nse_fo' },
  { name: 'FINNIFTY', segment: 'nse_fo' },
  { name: 'MIDCPNIFTY', segment: 'nse_fo' },
  { name: 'SENSEX', segment: 'bse_fo' },
];

const CHAIN_POLL_MS = 15_000;

/** Indian-style compact OI: 4.2L, 1.35Cr. */
function compactOi(value: string | number): string {
  const n = toNum(value);
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(1)}L`;
  return formatQty(n);
}

function OiBar({ value, max, side }: { value: number; max: number; side: 'ce' | 'pe' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className={cn('flex h-1.5 w-full overflow-hidden rounded-full bg-surface-3', side === 'ce' ? 'justify-end' : '')}>
      <div className={cn('h-full', side === 'ce' ? 'bg-sell/70' : 'bg-buy/70')} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function OptionChainPage() {
  const [segment, setSegment] = useState('nse_fo');
  const [underlying, setUnderlying] = useState('NIFTY');
  const [expiries, setExpiries] = useState<ExpiryInfo[]>([]);
  const [expiry, setExpiry] = useState('');
  const [strikesEachSide, setStrikesEachSide] = useState(10);
  const [chain, setChain] = useState<ChainData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<UnderlyingInfo[]>([]);
  const [legs, setLegs] = useState<StrategyLeg[]>([]);
  const [presetLots, setPresetLots] = useState(1);
  const [saved, setSaved] = useState<SavedStrategy[]>([]);
  const [connections, setConnections] = useState<BrokerStatus[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const subscribedRef = useRef<string[]>([]);

  const ticks = useMarketStore((s) => s.ticks);
  const { isConnected, feedAttached, feedMessage, subscribe, unsubscribe } = useMarketWebSocket();

  const activeConnection = useMemo(
    () => connections.find((c) => c.is_active && c.is_session_valid) ?? null,
    [connections]
  );

  // ── Loaders ───────────────────────────────────────────────────────────

  useEffect(() => {
    api.get('/brokers/status').then((r) => setConnections(r.data)).catch(() => setConnections([]));
  }, []);

  const fetchSaved = useCallback(async () => {
    try {
      const { data } = await api.get('/options/strategies/saved');
      setSaved(data);
    } catch {
      /* templates are optional */
    }
  }, []);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/options/expiries', { params: { underlying, segment } })
      .then(({ data }) => {
        if (cancelled) return;
        setExpiries(data);
        if (data.length > 0) setExpiry(data[0].expiry);
        else {
          setLoading(false);
          setError(`No upcoming option expiries found for ${underlying}.`);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        setError(getErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [underlying, segment]);

  const fetchChain = useCallback(async () => {
    if (!expiry) return;
    try {
      const { data } = await api.get('/options/chain', {
        params: { underlying, segment, expiry, strikes: strikesEachSide },
      });
      setChain(data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [underlying, segment, expiry, strikesEachSide]);

  useEffect(() => {
    if (!expiry) return;
    fetchChain();
    const timer = setInterval(fetchChain, CHAIN_POLL_MS);
    return () => clearInterval(timer);
  }, [fetchChain, expiry]);

  // Stream the visible strikes; drop the previous set when the window moves.
  useEffect(() => {
    if (!isConnected || !chain) return;
    const next = chain.tokens;
    const prev = subscribedRef.current;
    const gone = prev.filter((t) => !next.includes(t));
    if (gone.length) unsubscribe(gone);
    subscribe(next);
    subscribedRef.current = next;
  }, [isConnected, chain, subscribe, unsubscribe]);

  // Underlying search (stocks)
  useEffect(() => {
    if (!search.trim()) return;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get('/options/underlyings', { params: { segment, q: search.trim() } });
        setSearchResults(data.slice(0, 12));
      } catch {
        setSearchResults([]);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [search, segment]);

  // ── Live overlay ──────────────────────────────────────────────────────

  const live = useCallback(
    (q: ChainQuote | null): ChainQuote | null => {
      if (!q) return null;
      const t = ticks[q.instrument_token];
      if (!t || toNum(t.ltp) <= 0) return q;
      const baseline = toNum(q.oi) - toNum(q.oi_change);
      const liveOi = toNum(t.open_interest) > 0 ? toNum(t.open_interest) : toNum(q.oi);
      return {
        ...q,
        ltp: t.ltp,
        change_percent: toNum(t.change_percent) !== 0 ? t.change_percent : q.change_percent,
        oi: String(liveOi),
        oi_change: String(liveOi - baseline),
        volume: toNum(t.volume) > 0 ? t.volume : q.volume,
      };
    },
    [ticks]
  );

  const rows = useMemo(() => (chain ? chain.rows.map((r) => ({ ...r, ce: live(r.ce), pe: live(r.pe) })) : []), [chain, live]);
  const maxOi = useMemo(
    () => rows.reduce((m, r) => Math.max(m, toNum(r.ce?.oi), toNum(r.pe?.oi)), 0),
    [rows]
  );
  const spot = chain?.spot ? toNum(chain.spot) : null;

  // ── Leg actions ───────────────────────────────────────────────────────

  const addLeg = (row: ChainRow, type: 'CE' | 'PE', side: 'B' | 'S') => {
    const q = type === 'CE' ? row.ce : row.pe;
    if (!q || !chain) return;
    setLegs((current) => [
      ...current,
      {
        id: newLegId(),
        side,
        option_type: type,
        strike: toNum(row.strike),
        price: toNum(q.ltp),
        lots: presetLots,
        instrument_token: q.instrument_token,
        trading_symbol: q.trading_symbol,
        exchange_segment: q.exchange_segment,
      },
    ]);
  };

  const applyPreset = (kind: PresetKind) => {
    if (!chain) return;
    const built = buildPreset(kind, rows, presetLots);
    if (built.length === 0) {
      setNotice('Could not build that preset — the ATM strike is not in the current window.');
      return;
    }
    setLegs(built);
  };

  const loadSaved = (template: SavedStrategy) => {
    if (!chain) return;
    const built: StrategyLeg[] = [];
    const missing: string[] = [];
    for (const leg of template.legs) {
      const row = rows.find((r) => toNum(r.strike) === toNum(leg.strike));
      const q = row ? (leg.option_type === 'CE' ? row.ce : row.pe) : null;
      if (!row || !q) {
        missing.push(`${leg.strike} ${leg.option_type}`);
        continue;
      }
      built.push({
        id: newLegId(),
        side: leg.side,
        option_type: leg.option_type,
        strike: toNum(row.strike),
        price: toNum(q.ltp),
        lots: leg.lots,
        instrument_token: q.instrument_token,
        trading_symbol: q.trading_symbol,
        exchange_segment: q.exchange_segment,
      });
    }
    setLegs(built);
    setNotice(
      missing.length
        ? `Loaded "${template.name}"; ${missing.join(', ')} not in the current window — widen the strikes.`
        : `Loaded "${template.name}" onto the ${chain.expiry_label} chain.`
    );
  };

  const deleteSaved = async (id: string) => {
    try {
      await api.delete(`/options/strategies/saved/${id}`);
      fetchSaved();
    } catch (err) {
      setNotice(getErrorMessage(err));
    }
  };

  const pickUnderlying = (name: string, seg: string) => {
    setLegs([]);
    setSearch('');
    setSearchResults([]);
    setExpiries([]);
    setExpiry('');
    setChain(null);
    setLoading(true);
    setError(null);
    setSegment(seg);
    setUnderlying(name);
  };

  const onSearchChange = (value: string) => {
    setSearch(value);
    if (!value.trim()) setSearchResults([]);
  };

  const pcr = chain?.pcr ? toNum(chain.pcr) : null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Layers}
        title="Option Chain"
        description="Strikes around the money with OI, PCR and max pain — click B/S to build a strategy"
        meta={
          <>
            <span title={feedMessage || undefined}>
              <Badge
                tone={isConnected && feedAttached ? 'success' : 'warning'}
                icon={isConnected && feedAttached ? Wifi : WifiOff}
              >
                {isConnected && feedAttached ? 'Streaming' : isConnected && feedAttached === false ? 'No broker feed' : 'Snapshot only'}
              </Badge>
            </span>
            {activeConnection ? (
              <Badge tone="success">{activeConnection.display_name}</Badge>
            ) : (
              <Badge tone="danger">No broker session</Badge>
            )}
            {chain && <span className="text-xs text-fg-subtle">Updated {formatDateTime(chain.updated_at)}</span>}
          </>
        }
        actions={
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={fetchChain}>
            Refresh
          </Button>
        }
      />

      {notice && (
        <Alert tone="info" onDismiss={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {/* Controls */}
      <Card>
        <CardBody className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {QUICK_PICKS.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => pickUnderlying(p.name, p.segment)}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                  underlying === p.name
                    ? 'border-brand/40 bg-brand/12 text-brand-fg'
                    : 'border-line-strong bg-surface-2 text-fg-muted hover:text-fg'
                )}
              >
                {p.name}
              </button>
            ))}
            <div className="relative min-w-56 flex-1">
              <Input
                icon={Search}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Stock options: RELIANCE, HDFCBANK…"
                aria-label="Search underlyings"
              />
              {searchResults.length > 0 && (
                <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-line bg-surface-3 shadow-popover">
                  {searchResults.map((u) => (
                    <li key={`${u.segment}-${u.name}`}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-2"
                        onClick={() => pickUnderlying(u.name, u.segment)}
                      >
                        <span className="text-fg">{u.name}</span>
                        <span className="text-xs text-fg-faint">
                          {u.kind} · lot {u.lot_size}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-fg-muted">
              Expiry
              <Select value={expiry} onChange={(e) => setExpiry(e.target.value)} disabled={expiries.length === 0}>
                {expiries.map((e) => (
                  <option key={e.expiry} value={e.expiry}>
                    {e.label} · {e.days_to_expiry}d
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-fg-muted">
              Strikes each side
              <Select value={strikesEachSide} onChange={(e) => setStrikesEachSide(Number(e.target.value))}>
                {[5, 10, 15, 20, 30].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-fg-muted">
              Preset
              <Select value="" onChange={(e) => e.target.value && applyPreset(e.target.value as PresetKind)} disabled={!chain}>
                <option value="">Build a strategy…</option>
                {PRESETS.map((p) => (
                  <option key={p.kind} value={p.kind}>
                    {p.label} — {p.hint}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-fg-muted">
              Lots per leg
              <Input type="number" min={1} mono value={presetLots} onChange={(e) => setPresetLots(Math.max(1, Number(e.target.value) || 1))} />
            </label>
          </div>

          {saved.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-line pt-3">
              <span className="mr-1 inline-flex items-center gap-1 text-xs text-fg-faint">
                <Bookmark className="h-3.5 w-3.5" aria-hidden="true" /> Saved
              </span>
              {saved.map((t) => (
                <span key={t.id} className="inline-flex items-center overflow-hidden rounded-md border border-line-strong bg-surface-2 text-xs">
                  <button type="button" className="px-2 py-1 text-fg-muted hover:text-fg" onClick={() => loadSaved(t)} title={`${t.underlying} · ${t.legs.length} legs`}>
                    {t.name}
                  </button>
                  <button
                    type="button"
                    className="border-l border-line px-1.5 py-1 text-fg-faint hover:text-danger"
                    onClick={() => deleteSaved(t.id)}
                    aria-label={`Delete saved strategy ${t.name}`}
                  >
                    <Trash2 className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {error && (
        <Alert
          tone="danger"
          title="Couldn't load the option chain"
          action={
            <Button variant="secondary" size="sm" onClick={fetchChain}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Metrics */}
      {chain && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
          {[
            ['Spot', spot ? formatNumber(spot) : '—', chain.spot_source !== 'unknown' ? `via ${chain.spot_source}` : ''],
            ['ATM', chain.atm_strike ? formatNumber(chain.atm_strike) : '—', ''],
            ['PCR', pcr !== null ? pcr.toFixed(2) : '—', pcr === null ? '' : pcr > 1.2 ? 'put-heavy' : pcr < 0.8 ? 'call-heavy' : 'balanced'],
            ['Max pain', chain.max_pain ? formatNumber(chain.max_pain) : '—', ''],
            ['Support', chain.support ? formatNumber(chain.support) : '—', 'highest PE OI'],
            ['Resistance', chain.resistance ? formatNumber(chain.resistance) : '—', 'highest CE OI'],
            ['Lot size', formatQty(chain.lot_size), `${chain.strikes_total} strikes`],
          ].map(([label, value, hint]) => (
            <div key={label} className="rounded-xl border border-line bg-surface px-3 py-2.5">
              <p className="text-xs text-fg-subtle">{label}</p>
              <p className="font-tabular text-base font-semibold text-fg">{value}</p>
              {hint && <p className="text-xs text-fg-faint">{hint}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* Chain */}
        <Card className="xl:col-span-2">
          <CardHeader
            title={chain ? `${chain.underlying} · ${chain.expiry_label}` : 'Option chain'}
            description={chain ? `${rows.length} strikes · ITM shaded · ATM highlighted` : 'Pick an underlying and expiry'}
          />
          <TableWrap>
            <Table className="text-xs">
              <THead>
                <TR>
                  <TH colSpan={5} align="center" className="border-r border-line text-sell">
                    CALLS
                  </TH>
                  <TH align="center">Strike</TH>
                  <TH colSpan={5} align="center" className="border-l border-line text-buy">
                    PUTS
                  </TH>
                </TR>
                <TR>
                  <TH align="right">OI</TH>
                  <TH align="right">OI Δ</TH>
                  <TH align="right">Vol</TH>
                  <TH align="right">LTP</TH>
                  <TH align="center" className="border-r border-line">
                    Trade
                  </TH>
                  <TH align="center" />
                  <TH align="center" className="border-l border-line">
                    Trade
                  </TH>
                  <TH align="right">LTP</TH>
                  <TH align="right">Vol</TH>
                  <TH align="right">OI Δ</TH>
                  <TH align="right">OI</TH>
                </TR>
              </THead>
              <TBody>
                {loading && !chain ? (
                  <TableMessage colSpan={11}>
                    <div className="flex flex-col gap-2 p-4">
                      <Skeleton className="h-7" />
                      <Skeleton className="h-7" />
                      <Skeleton className="h-7" />
                      <Skeleton className="h-7" />
                    </div>
                  </TableMessage>
                ) : rows.length === 0 ? (
                  <TableMessage colSpan={11}>
                    <EmptyState
                      icon={Layers}
                      title="No chain to show"
                      description={activeConnection ? 'Choose an underlying with listed options.' : 'Connect your broker in Settings to load the instrument master and quotes.'}
                    />
                  </TableMessage>
                ) : (
                  rows.map((row) => {
                    const ce = row.ce;
                    const pe = row.pe;
                    return (
                      <TR key={row.strike} className={cn(row.is_atm && 'bg-brand/8')}>
                        {/* CE side */}
                        <TD align="right" className={cn('font-tabular', ce?.in_the_money && 'bg-surface-2')}>
                          <div className="text-fg-muted">{ce ? compactOi(ce.oi) : '—'}</div>
                          {ce && <OiBar value={toNum(ce.oi)} max={maxOi} side="ce" />}
                        </TD>
                        <TD align="right" className={cn('font-tabular', ce?.in_the_money && 'bg-surface-2', signClass(ce?.oi_change))}>
                          {ce && toNum(ce.oi_change) !== 0 ? `${toNum(ce.oi_change) > 0 ? '+' : ''}${compactOi(Math.abs(toNum(ce.oi_change)))}` : '—'}
                        </TD>
                        <TD align="right" className={cn('font-tabular text-fg-subtle', ce?.in_the_money && 'bg-surface-2')}>
                          {ce ? compactOi(ce.volume) : '—'}
                        </TD>
                        <TD align="right" className={cn('font-tabular', ce?.in_the_money && 'bg-surface-2')}>
                          <div className="font-medium text-fg">{ce ? formatNumber(ce.ltp) : '—'}</div>
                          {ce && <div className={cn('text-[11px]', signClass(ce.change_percent))}>{formatPct(ce.change_percent)}</div>}
                        </TD>
                        <TD align="center" className={cn('border-r border-line', ce?.in_the_money && 'bg-surface-2')}>
                          {ce && (
                            <div className="flex justify-center gap-1">
                              <button type="button" className="rounded border border-buy/30 bg-buy/12 px-1.5 py-0.5 font-semibold text-buy hover:bg-buy/25" onClick={() => addLeg(row, 'CE', 'B')} aria-label={`Buy ${ce.trading_symbol}`}>B</button>
                              <button type="button" className="rounded border border-sell/30 bg-sell/12 px-1.5 py-0.5 font-semibold text-sell hover:bg-sell/25" onClick={() => addLeg(row, 'CE', 'S')} aria-label={`Sell ${ce.trading_symbol}`}>S</button>
                            </div>
                          )}
                        </TD>
                        {/* Strike */}
                        <TD align="center" className="font-tabular font-semibold text-fg">
                          {formatNumber(row.strike)}
                          {row.is_atm && <div className="text-[10px] font-medium text-brand-fg">ATM</div>}
                        </TD>
                        {/* PE side */}
                        <TD align="center" className={cn('border-l border-line', pe?.in_the_money && 'bg-surface-2')}>
                          {pe && (
                            <div className="flex justify-center gap-1">
                              <button type="button" className="rounded border border-buy/30 bg-buy/12 px-1.5 py-0.5 font-semibold text-buy hover:bg-buy/25" onClick={() => addLeg(row, 'PE', 'B')} aria-label={`Buy ${pe.trading_symbol}`}>B</button>
                              <button type="button" className="rounded border border-sell/30 bg-sell/12 px-1.5 py-0.5 font-semibold text-sell hover:bg-sell/25" onClick={() => addLeg(row, 'PE', 'S')} aria-label={`Sell ${pe.trading_symbol}`}>S</button>
                            </div>
                          )}
                        </TD>
                        <TD align="right" className={cn('font-tabular', pe?.in_the_money && 'bg-surface-2')}>
                          <div className="font-medium text-fg">{pe ? formatNumber(pe.ltp) : '—'}</div>
                          {pe && <div className={cn('text-[11px]', signClass(pe.change_percent))}>{formatPct(pe.change_percent)}</div>}
                        </TD>
                        <TD align="right" className={cn('font-tabular text-fg-subtle', pe?.in_the_money && 'bg-surface-2')}>
                          {pe ? compactOi(pe.volume) : '—'}
                        </TD>
                        <TD align="right" className={cn('font-tabular', pe?.in_the_money && 'bg-surface-2', signClass(pe?.oi_change))}>
                          {pe && toNum(pe.oi_change) !== 0 ? `${toNum(pe.oi_change) > 0 ? '+' : ''}${compactOi(Math.abs(toNum(pe.oi_change)))}` : '—'}
                        </TD>
                        <TD align="right" className={cn('font-tabular', pe?.in_the_money && 'bg-surface-2')}>
                          <div className="text-fg-muted">{pe ? compactOi(pe.oi) : '—'}</div>
                          {pe && <OiBar value={toNum(pe.oi)} max={maxOi} side="pe" />}
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </TableWrap>
        </Card>

        {/* Builder */}
        <Card className="h-fit xl:sticky xl:top-4">
          <CardHeader
            title="Strategy builder"
            description={legs.length ? `${legs.length} leg${legs.length === 1 ? '' : 's'} · lot ${chain?.lot_size ?? '—'}` : 'Pick legs from the chain or a preset'}
          />
          <CardBody>
            <StrategyBuilder
              legs={legs}
              lotSize={chain?.lot_size ?? 1}
              spot={spot}
              underlying={underlying}
              segment={segment}
              expiry={chain?.expiry ?? expiry}
              connectionId={activeConnection?.connection_id ?? null}
              onChange={setLegs}
              onSaved={fetchSaved}
              onPlaced={(result) => setNotice(`${result.name}: ${result.placed} of ${result.legs.length} legs sent. Track them on the Orders page.`)}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
